"use client"

import { Button } from "@/components/ui/button"
import { Pause, Play, Volume2, VolumeX } from "@/lib/icons"
import { useTranslations } from "next-intl"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface AudioControlsBarProps {
  audioManager: any
  audioProgress: number
  audioVolume: number
  hasAudio: boolean
  isAudioMode: boolean
  isAudioMuted: boolean
  isAudioPlaying: boolean
  setAudioVolume: React.Dispatch<React.SetStateAction<number>>
  setIsAudioMode: React.Dispatch<React.SetStateAction<boolean>>
  setIsAudioMuted: React.Dispatch<React.SetStateAction<boolean>>
  setIsAudioPlaying: React.Dispatch<React.SetStateAction<boolean>>
}

export function AudioControlsBar({
  audioManager,
  audioProgress,
  audioVolume,
  hasAudio,
  isAudioMode,
  isAudioMuted,
  isAudioPlaying,
  setAudioVolume,
  setIsAudioMode,
  setIsAudioMuted,
  setIsAudioPlaying,
}: AudioControlsBarProps) {
  const tt = useTranslations("booruDetailModal")
  return (
    <>
      {hasAudio && audioManager && (
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/80 rounded-full px-4 py-2 flex items-center gap-3 pointer-events-auto z-20">
                  {/* Audio Mode Toggle */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-8 w-8 rounded-full ${
                      isAudioMode ? "text-green-400" : "text-white/60"
                    }`}
                    onClick={() => {
                      setIsAudioMode(!isAudioMode)
                      if (!isAudioMode && audioManager) {
                        audioManager.stop()
                      }
                    }}
                    title={isAudioMode ? tt("audioModeOn") : tt("audioModeOff")}
                  >
                    {isAudioMode ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  </Button>
                  
                  {isAudioMode && (
                    <>
                      {/* Play/Pause */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full text-white/80 hover:text-white"
                        onClick={async () => {
                          if (audioManager.isPaused()) {
                            await audioManager.resume()
                            setIsAudioPlaying(true)
                          } else {
                            audioManager.pause()
                            setIsAudioPlaying(false)
                          }
                        }}
                        title={isAudioPlaying ? tt("pause") : tt("play")}
                      >
                        {isAudioPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </Button>
                      
                      {/* Mute Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-white/80 hover:text-white"
                        onClick={() => {
                          const newMuted = !isAudioMuted
                          setIsAudioMuted(newMuted)
                          audioManager.setMuted(newMuted)
                        }}
                        title={isAudioMuted ? tt("unmute") : tt("mute")}
                      >
                        {isAudioMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                      </Button>
                      
                      {/* Volume Slider */}
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={audioVolume * 100}
                        onChange={(e) => {
                          const vol = parseInt(e.target.value) / 100
                          setAudioVolume(vol)
                          audioManager.setVolume(vol)
                        }}
                        className="w-20 h-1 bg-white/20 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #4ade80 0%, #4ade80 ${audioVolume * 100}%, rgba(255,255,255,0.2) ${audioVolume * 100}%, rgba(255,255,255,0.2) 100%)`
                        }}
                        title={tt("volume")}
                      />
                      
                      {/* Audio Progress */}
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <span>{(() => {
                          const current = audioManager.getCurrentTime()
                          const mins = Math.floor(current / 60)
                          const secs = Math.floor(current % 60)
                          return `${mins}:${secs.toString().padStart(2, '0')}`
                        })()}</span>
                        <div className="w-32 h-1 bg-white/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-400 transition-all"
                            style={{ width: `${audioProgress}%` }}
                          />
                        </div>
                        <span>{(() => {
                          const duration = audioManager.getDuration()
                          if (!isFinite(duration)) return '0:00'
                          const mins = Math.floor(duration / 60)
                          const secs = Math.floor(duration % 60)
                          return `${mins}:${secs.toString().padStart(2, '0')}`
                        })()}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
    </>
  )
}
